// Razorpay Affordability widget (EMI / Cardless / Pay Later offers).
// Lazy-loads the Razorpay CDN script exactly once, renders into
// #razorpay-affordability-widget, and re-renders whenever the selected
// variant price changes. Themed to the Loadout dark palette via the
// documented display.widget.main customisation keys.
//
// Key ID is the public Razorpay key (safe client-side; same key the
// checkout flow uses). Amount must be the FINAL amount in paise.

import { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://cdn.razorpay.com/widgets/affordability/affordability.js";
const KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

let scriptPromise = null;
function loadScript() {
        if (window.RazorpayAffordabilitySuite) return Promise.resolve();
        if (scriptPromise) return scriptPromise;
        scriptPromise = new Promise((resolve, reject) => {
                const s = document.createElement("script");
                s.src = SCRIPT_SRC;
                s.async = true;
                s.onload = () => resolve();
                s.onerror = () => {
                        scriptPromise = null; // allow retry on next mount
                        reject(new Error("affordability script load failed"));
                };
                document.head.appendChild(s);
        });
        return scriptPromise;
}

export default function AffordabilityWidget({ amountInPaise }) {
        const ref = useRef(null);

        useEffect(() => {
                if (!KEY || !amountInPaise || amountInPaise <= 0) return undefined;
                let cancelled = false;
                loadScript()
                        .then(() => {
                                if (cancelled || !ref.current) return;
                                // Clear any previous render before re-init (variant price change).
                                ref.current.innerHTML = "";
                                const widgetConfig = {
                                        key: KEY,
                                        amount: amountInPaise,
                                        theme: { color: "#FF9F1C" },
                                        display: {
                                                widget: {
                                                        main: {
                                                                content: {
                                                                        backgroundColor: "#0A0A0A",
                                                                        color: "#FFFFFF",
                                                                        fontSize: "13px",
                                                                },
                                                                heading: { color: "#FFFFFF", fontSize: "13px" },
                                                                footer: { color: "#8A8A8A", fontSize: "12px", darkLogo: true },
                                                                link: { button: false, color: "#FF9F1C", fontSize: "12px" },
                                                                discount: { color: "#FF9F1C" },
                                                                isDarkMode: true,
                                                        },
                                                },
                                        },
                                };
                                try {
                                        new window.RazorpayAffordabilitySuite(widgetConfig).render();
                                } catch {
                                        /* widget render failed — stay silent, PDP unaffected */
                                }
                        })
                        .catch(() => {});
                return () => {
                        cancelled = true;
                };
        }, [amountInPaise]);

        if (!KEY) return null;
        return (
                <div
                        id="razorpay-affordability-widget"
                        ref={ref}
                        data-testid="pdp-affordability-widget"
                        className="mt-4"
                />
        );
}
